import { faMicrophone } from "@fortawesome/pro-solid-svg-icons";
import {
  AccessibleSelect,
  Button,
  Checkbox,
  Icon,
  InputControl,
  SelectOptionAtom,
  Text,
} from "@growtherapy/sprout-ui";
import { useAtomValue } from "jotai";
import { useCallback, useState } from "react";
import { useDebounceCallback } from "usehooks-ts";
import speakerTestChimeUri from "../../assets/audio/speaker-test-chime.mp3?url";
import { usePlayAudioCallback } from "../../hooks/usePlayAudioCallback";
import { NoAccessVariant } from "../../session/controls";
import { useNoiseCancellationCallbacks } from "../../session/controls/useNoiseCancellationCallbacks";
import {
  getDeviceOptionsFromDeviceList,
  useUpdateAvailableDevicesInterval,
} from "../../session/devices/useDevices";
import { useGetInputVolume } from "../../session/useGetInputVolume";
import { toast } from "../../toasts";
import {
  LocalMediaStatus,
  currentMicrophoneIdAtom,
  getStoredAudioVideoSettings,
  localAudioStatusAtom,
  useStoredAudioVideoSettings,
  useSwitchMicrophone,
} from "../../video-sdk";
import {
  availableDevicesAtom,
  isKrispNoiseCancellationSupported as isKrispNoiseCancellationSupported_,
  permissionDeniedAtom,
} from "../../video-sdk/twilio";
import { VolumeMonitor } from "../VolumeMonitor";
import { BlockedPermissionMessageWrapper as BlockedPermissionMessage } from "./BlockedPermissionMessage";
import { useAudioOutput } from "../../video-sdk/actions/useAudioActions";
import { noop } from "../../utils";
import { logger } from "../../datadog/logger";
import { useIsZoomEnabled } from "../../hooks";

export function AudioSettingsTab({
  microphoneIsBlocked,
  microphoneIsLoading,
  outputIsLoading,
  inputVolume,
  outputVolume,
  playTestAudio,
  inputOptions,
  outputOptions,
  setMicrophone,
  setOutputDevice,
  selectedMicrophone,
  selectedOutputDevice,
  audioIsPlaying,
  toggleNoiseCancellation,
  noiseCancellationIsEnabled,
  isNoiseCancellationSupported,
}: {
  microphoneIsBlocked: boolean;
  microphoneIsLoading?: boolean;
  outputIsLoading?: boolean;
  audioOutputDeviceId?: string;
  /**
   * Input volume level from 0 to 1. Hides the volume monitor if undefined.
   */
  inputVolume?: number;
  outputVolume: number;
  playTestAudio: () => void;
  inputOptions: SelectOptionAtom[];
  outputOptions: SelectOptionAtom[];
  setMicrophone: (selectedId: string) => void;
  setOutputDevice: (selectedId: string) => void | Promise<void>;
  selectedMicrophone?: string;
  selectedOutputDevice?: string;
  audioIsPlaying?: boolean;
  toggleNoiseCancellation?: () => Promise<void> | void;
  noiseCancellationIsEnabled?: boolean;
  isNoiseCancellationSupported?: boolean;
}) {
  const shouldDisableFields = microphoneIsBlocked || microphoneIsLoading;
  return (
    <>
      <div className="pb-6 border-b border-neutral-500">
        {outputOptions.length > 0 && (
          <InputControl disabled={outputIsLoading} label="Speakers">
            <AccessibleSelect
              onChange={setOutputDevice}
              options={outputOptions}
              value={selectedOutputDevice ?? outputOptions[0]?.value}
            />
          </InputControl>
        )}
        <div className="flex justify-between">
          <Button
            use="secondary"
            size="sm"
            onClick={() => playTestAudio()}
            disabled={audioIsPlaying}
          >
            Test speakers
          </Button>
          <VolumeMonitor volume={outputVolume} />
        </div>
      </div>
      <div className="border-t border-neutral-200 pt-6">
        <InputControl label="Microphone" disabled={shouldDisableFields}>
          <AccessibleSelect
            options={inputOptions}
            value={selectedMicrophone ?? inputOptions[0]?.value}
            onChange={(value: string) => setMicrophone(value)}
            placeholder={microphoneIsBlocked ? "No access" : "Select"}
          />
        </InputControl>
        {inputVolume !== undefined && (
          <div className="flex justify-between pb-6">
            <Text variant="sm" className="text-neutral-700 font-medium">
              <Icon aria-hidden icon={faMicrophone} className="mr-2" />
              Speak to test
            </Text>
            <VolumeMonitor volume={inputVolume} />
          </div>
        )}
        {microphoneIsBlocked && (
          <BlockedPermissionMessage
            noAccessVariant={NoAccessVariant.Microphone}
          />
        )}
        {isNoiseCancellationSupported && (
          <InputControl aria-label="toggle noise Cancellation">
            <Checkbox
              onChange={() => toggleNoiseCancellation?.()}
              checked={noiseCancellationIsEnabled}
              label="Reduce background noise"
            />
          </InputControl>
        )}
      </div>
    </>
  );
}

export function AudioSettingsTabWrapper() {
  useUpdateAvailableDevicesInterval();

  const devices = useAtomValue(availableDevicesAtom);
  const { microphoneIsBlocked } = useAtomValue(permissionDeniedAtom);
  const localAudioStatus = useAtomValue(localAudioStatusAtom);
  const microphoneIsLoading =
    !localAudioStatus || localAudioStatus === LocalMediaStatus.LOADING;
  const [isAudioOutputLoading, setAudioOutputLoading] = useState(false);

  const [isZoomEnabled] = useIsZoomEnabled();
  const inputVolume = useGetInputVolume({
    // Override the default smoothing function to be a no-op when Zoom is
    // enabled
    smoothingFunction: isZoomEnabled ? noop : undefined,
  });
  const { audioOutputDeviceId, switchAudioOutputDevice } = useAudioOutput();
  const handleSwitchingAudioOutputDevice = useCallback(
    async (deviceId: string) => {
      setAudioOutputLoading(true);
      await switchAudioOutputDevice({
        deviceId,
      })
        .catch((error: Error) => {
          toast.error("Unable to select speaker device.");
          logger.error(
            "Failed to switch audio output device",
            { deviceId },
            error,
          );
        })
        .finally(() => {
          setAudioOutputLoading(false);
        });
    },
    [switchAudioOutputDevice],
  );

  const { storedAudioVideoSettings } = useStoredAudioVideoSettings();
  const selectedMicrophone = useAtomValue(currentMicrophoneIdAtom);
  const { playAudio, volume, isPlaying } =
    usePlayAudioCallback(speakerTestChimeUri);
  const inputOptions = getDeviceOptionsFromDeviceList(
    devices?.audioInputDevices,
  );
  const outputOptions = getDeviceOptionsFromDeviceList(
    devices?.audioOutputDevices,
  );
  const { enableNoiseCancellation, disableNoiseCancellation } =
    useNoiseCancellationCallbacks();

  const switchMicrophone = useSwitchMicrophone();

  // This debounce shouldn't be strictly necessary, but it seems like the select
  // component can call its onChange twice on Android devices which causes
  // unexpected behavior
  const setMicrophone = useDebounceCallback(
    useCallback(
      async (selectedId: string) => {
        if (!(await switchMicrophone(selectedId))) {
          toast.error("Something went wrong selecting your microphone device.");
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    100,
  );

  const toggleNoiseCancellation = useDebounceCallback(
    useCallback(
      async () => {
        const { noiseCancellationIsEnabled: noiseCancellationIsEnabled } =
          getStoredAudioVideoSettings();
        let result;
        if (noiseCancellationIsEnabled) {
          result = await disableNoiseCancellation();
        } else {
          result = await enableNoiseCancellation();
        }
        if (result.status === "error") {
          toast.error("Failed to toggle noise cancellation");
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [],
    ),
    100,
  );

  const correspondingMicrophoneId = inputOptions.some(
    (option) => option.value === selectedMicrophone,
  )
    ? selectedMicrophone
    : storedAudioVideoSettings?.inputAudioDeviceId;

  return (
    <AudioSettingsTab
      inputVolume={inputVolume}
      outputVolume={volume}
      playTestAudio={() => playAudio(audioOutputDeviceId)}
      audioIsPlaying={isPlaying}
      inputOptions={inputOptions}
      outputOptions={outputOptions}
      outputIsLoading={isAudioOutputLoading}
      selectedMicrophone={correspondingMicrophoneId}
      setMicrophone={setMicrophone}
      selectedOutputDevice={audioOutputDeviceId}
      setOutputDevice={handleSwitchingAudioOutputDevice}
      microphoneIsBlocked={microphoneIsBlocked}
      microphoneIsLoading={microphoneIsLoading}
      toggleNoiseCancellation={toggleNoiseCancellation}
      noiseCancellationIsEnabled={
        storedAudioVideoSettings.noiseCancellationIsEnabled
      }
      isNoiseCancellationSupported={
        !isZoomEnabled && isKrispNoiseCancellationSupported_
      }
    />
  );
}
